/**
 * Client for the grounded reply-comment drafting endpoint (Prosumer/Pro).
 * Backend: POST /api/proceedings/{docket}/draft-reply (see reply_draft.py).
 * The draft is grounded in the proceeding's analyzed record — a bounded function,
 * not a chat.
 */
import { supabase } from './supabase';

function getBaseUrl(): string {
  const raw = (import.meta as any)?.env?.VITE_RAG_API_BASE_URL as string | undefined;
  return (raw || 'https://fcc-summarize-app-r4ac3.ondigitalocean.app').replace(/\/+$/, '');
}

export type DraftStance = 'support' | 'oppose' | 'modify';

export interface DraftOptions {
  stance: DraftStance;
  issue_ids?: string[];
  party?: string;
  notes?: string;
}

export interface ReplyDraft {
  docket: string;
  stance: string;
  draft_markdown: string;
  issues_engaged: string[];
  cited_filers: string[];
  cited_authorities: string[];
  disclaimer: string;
}

/** Coded errors so the UI can react (sign in / upgrade / limit) without string-matching. */
export class DraftError extends Error {
  code: 'AUTH' | 'TIER' | 'LIMIT' | 'NO_DIGEST' | 'FAILED';
  constructor(code: DraftError['code'], message: string) {
    super(message);
    this.code = code;
  }
}

/** Shared POST to a reply endpoint with auth + coded-error mapping. */
async function postReply<T>(docket: string, path: string, body: unknown): Promise<T> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) throw new DraftError('AUTH', 'Please sign in to draft a reply.');

  const res = await fetch(
    `${getBaseUrl()}/api/proceedings/${encodeURIComponent(docket)}/${path}`,
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(body ?? {}),
    },
  );

  if (res.status === 401) throw new DraftError('AUTH', 'Please sign in to draft a reply.');
  if (res.status === 403) throw new DraftError('TIER', 'Reply drafting is a Prosumer & Pro feature.');
  if (res.status === 429) {
    const b = await res.json().catch(() => ({}));
    throw new DraftError('LIMIT', b.detail || 'You’ve used all your reply drafts this month.');
  }
  if (res.status === 409) throw new DraftError('NO_DIGEST', 'This proceeding isn’t analyzed yet — open its digest first.');
  if (!res.ok) {
    const b = await res.json().catch(() => ({}));
    throw new DraftError('FAILED', b.detail || 'Something went wrong. Please try again.');
  }
  return res.json();
}

export async function generateReplyDraft(docket: string, opts: DraftOptions): Promise<ReplyDraft> {
  return postReply<ReplyDraft>(docket, 'draft-reply', opts);
}

// ── Workbench (v1): outline → structured draft → localized refine ────────────

export interface ReplyConcept {
  id: string;
  issue?: string;
  title: string;
  summary?: string;
  raised_by?: string[];
  /** Institutional/expert voices to engage by name (0–2). */
  lead_voices?: string[];
  /** 'institutional' = a named org/expert leads it; 'public' = mass individual sentiment. */
  voice_type?: 'institutional' | 'public';
  /** How many filers were on the underlying issue (breadth). */
  n_supporters?: number;
}

export interface ReplyOutline {
  docket: string;
  concepts: ReplyConcept[];
  /** Concept ids that match the filer's position bullets — pre-checked in the UI. */
  suggested_ids?: string[];
}

export interface WorkbenchDraftOptions {
  stance: DraftStance;
  concepts?: ReplyConcept[];      // the outline concepts (echoed for grounding)
  concept_ids?: string[];         // which to engage
  who_you_are?: string;
  arguments?: string[];
  notes?: string;
}

export interface WorkbenchDraft {
  docket: string;
  stance: string;
  draft_markdown: string;
  sections?: { heading: string; concept_id?: string | null }[];
  placeholders?: string[];
  cited_filers: string[];
  disclaimer: string;
}

export interface RefineOptions {
  selection: string;
  instruction: string;
  before?: string;
  after?: string;
  who_you_are?: string;
}

/** Restructure the record into a checklist of engageable concepts. Pass the filer's
 *  position bullets to also get `suggested_ids` (concepts to pre-check). */
export function fetchReplyOutline(docket: string, bullets?: string[]): Promise<ReplyOutline> {
  return postReply<ReplyOutline>(docket, 'reply/outline', { arguments: bullets && bullets.length ? bullets : undefined });
}

/** Structured first draft from the chosen concepts + the filer's position/args. */
export function generateWorkbenchDraft(docket: string, opts: WorkbenchDraftOptions): Promise<WorkbenchDraft> {
  return postReply<WorkbenchDraft>(docket, 'reply/draft', opts);
}

/** Localized edit of a selected span — returns only the replacement text. */
export function refineReplySpan(docket: string, opts: RefineOptions): Promise<{ replacement: string }> {
  return postReply<{ replacement: string }>(docket, 'reply/refine', opts);
}
